/**
 * Pointer event handler for sculpt mode.
 *
 * Listens on the canvas DOM element for pointer events and drives the
 * sculpt brush engine. Pen input is always accepted; mouse and touch are
 * accepted only when the mode-local non-pen toggle is enabled.
 *
 * Apple Pencil Pro hover (pointerType "pen", no buttons pressed)
 * updates the brush cursor position for a preview.
 */

import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import type { SculptFalloffCurve } from "../app/storeTypes.ts";
import type { BrushCursorState } from "./BrushCursor.tsx";
import { resolveSculptPointerPressure } from "./sculptInput.ts";
import { makeScratch } from "./scratch.ts";
import { screenToObjectLocalRadius } from "./screenRadius.ts";

/**
 * Overlap-attenuation factor for dab-based strokes.
 *
 * Closely-spaced dabs overlap; without correction the accumulated
 * displacement at each dab centre far exceeds the inter-dab regions,
 * producing a visible scalloped "dabbing" pattern — most pronounced at
 * high mesh density where vertices faithfully resolve each dab's falloff
 * peak.  This factor scales per-dab strength down so the cumulative
 * result along a stroke equals the intended single-dab strength.
 *
 * Port of Blender's paint_stroke_integrate_overlap() (paint_stroke.cc).
 * Coordinate system: radius = 1.0, spacing = fraction of radius.
 */
function computeOverlapFactor(
  spacing: number,
  falloffCurve: SculptFalloffCurve,
): number {
  if (spacing >= 2.0) return 1.0; // spacing ≥ diameter → no overlap

  const h = spacing; // dab step size (radius units, radius = 1)
  const n = Math.ceil(2.0 / h); // dabs spanning one full diameter

  function evalFalloff(t: number): number {
    switch (falloffCurve) {
      case "sharp":
        return (1 - t) ** 0.4;
      case "constant":
        return 1.0;
      case "linear":
        return 1 - t;
      default:
        return (1 - t * t) ** 2; // smooth — (1−t²)²
    }
  }

  // Sample 10 positions across [0, 1] (half-brush) and find the maximum
  // accumulated falloff at any point when dabs repeat every h units.
  // (Mirrors Blender's m = 10 loop.)
  let maxOverlap = 0;
  for (let i = 0; i < 10; i++) {
    const x0 = i / 10 - 1; // position relative to left edge of brush
    let sum = 0;
    for (let j = 0; j < n; j++) {
      const xx = Math.abs(x0 + j * h);
      if (xx < 1.0) sum += evalFalloff(xx);
    }
    if (sum > maxOverlap) maxOverlap = sum;
  }

  return maxOverlap > 0 ? 1 / maxOverlap : 1.0;
}

export function SculptHandler({
  cursorRef,
}: {
  cursorRef: React.MutableRefObject<BrushCursorState>;
}) {
  const { gl, camera, scene, size: viewportSize } = useThree();
  const raycaster = useRef(new THREE.Raycaster()).current;
  const strokeActive = useRef(false);
  const overlapFactor = useRef(1.0);

  // ── Dab spacing state ──
  // Track the last dab position/normal/pressure so we can gate by distance
  // and interpolate intermediate dabs for smooth, uniform strokes.
  const lastDab = useRef<{
    center: [number, number, number];
    normal: [number, number, number];
    pressure: number;
  } | null>(null);

  // ── Lazy mouse stabilizer state ──
  // The smoothed position trails behind the raw pointer, producing cleaner
  // strokes.  Only active when sculptMode.lazyRadius > 0.
  const lazyPos = useRef<[number, number, number] | null>(null);
  const lazyNormal = useRef<[number, number, number] | null>(null);

  // ── Grab brush state ──
  // The grab brush projects screen-space pointer movement onto a
  // camera-perpendicular plane at the grab depth, producing a world-space
  // translation that is then converted to local space.  This lets the user
  // pull vertices away from the surface — not just push into it.
  const grabAnchorLocal = useRef<[number, number, number] | null>(null);
  const grabAnchorNormal = useRef<[number, number, number] | null>(null);
  const grabWorldOrigin = useRef<THREE.Vector3 | null>(null);
  /** Mesh reference captured at grab start for consistent local-space conversion. */
  const grabMesh = useRef<THREE.Mesh | null>(null);

  useEffect(() => {
    const canvas = gl.domElement;

    // Ensure touch-action: none so Safari doesn't intercept pen strokes
    const prevTouchAction = canvas.style.touchAction;
    canvas.style.touchAction = "none";

    // rAF coalescing — batch multiple pointermove events into one per frame
    let pendingMove: PointerEvent | null = null;
    let rafId: number | null = null;

    // Reusable Vector3s for barycentric computation, view direction, and
    // screenToPlane / worldVecToLocal / raycast NDC / grab delta helpers.
    // These closures are invoked on every pointer-move event (RAF-coalesced
    // to one per frame) and previously allocated fresh objects per call.
    const tmp = makeScratch({
      v0: "vec3",
      v1: "vec3",
      v2: "vec3",
      e0: "vec3",
      e1: "vec3",
      e2: "vec3",
      viewDir: "vec3",
      invMat: "mat4",
      invMat3: "mat3",
      planeNormal: "vec3",
      plane: "plane",
      planePt: "vec3",
      worldDelta: "vec3",
      ndc: "vec2",
    });

    /** Resolve the sculpt target mesh in the scene graph. */
    function getTargetMesh(): THREE.Mesh | null {
      const sm = useStore.getState().sculptMode;
      if (!sm) return null;
      let found: THREE.Mesh | null = null;
      scene.traverse((obj) => {
        if (found) return;
        if (
          obj instanceof THREE.Mesh &&
          obj.userData?.poserefObjectId === sm.objectId
        ) {
          found = obj;
        }
      });
      return found;
    }

    /** Raycast from screen coordinates to the target mesh. */
    function raycastToMesh(
      clientX: number,
      clientY: number,
      mesh: THREE.Mesh,
    ): THREE.Intersection | null {
      const rect = canvas.getBoundingClientRect();
      tmp.ndc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(tmp.ndc, camera);
      const hits = raycaster.intersectObject(mesh, false);
      return hits.length > 0 ? hits[0] : null;
    }

    /** Convert a world-space point to the mesh's object-local space. */
    function worldToLocal(
      point: THREE.Vector3,
      mesh: THREE.Mesh,
    ): [number, number, number] {
      const local = mesh.worldToLocal(point.clone());
      return [local.x, local.y, local.z];
    }

    /**
     * Convert a world-space vector to local space preserving magnitude.
     * (transformDirection normalises the result, losing the delta length.)
     * Reuses the shared tmp.invMat/tmp.invMat3 scratch objects — safe because
     * this is always called synchronously on the main thread.
     */
    function worldVecToLocal(
      vec: THREE.Vector3,
      mesh: THREE.Mesh,
    ): [number, number, number] {
      tmp.invMat.copy(mesh.matrixWorld).invert();
      tmp.invMat3.setFromMatrix4(tmp.invMat);
      const local = vec.clone().applyMatrix3(tmp.invMat3);
      return [local.x, local.y, local.z];
    }

    /** Get the face normal in object-local space. */
    function getLocalNormal(hit: THREE.Intersection): [number, number, number] {
      const face = hit.face;
      if (!face) return [0, 0, 1];
      return [face.normal.x, face.normal.y, face.normal.z];
    }

    /**
     * Compute the camera's forward direction in the mesh's object-local space.
     * Used by backface culling to filter vertices facing away from the camera.
     */
    function getViewDirectionLocal(mesh: THREE.Mesh): [number, number, number] {
      camera.getWorldDirection(tmp.viewDir);
      tmp.invMat.copy(mesh.matrixWorld).invert();
      tmp.invMat3.setFromMatrix4(tmp.invMat);
      tmp.viewDir.applyMatrix3(tmp.invMat3).normalize();
      return [tmp.viewDir.x, tmp.viewDir.y, tmp.viewDir.z];
    }

    /**
     * Project screen coordinates onto a plane perpendicular to the camera
     * that passes through `planeOrigin`.  Returns the world-space point.
     */
    function screenToPlane(
      clientX: number,
      clientY: number,
      planeOrigin: THREE.Vector3,
    ): THREE.Vector3 | null {
      const rect = canvas.getBoundingClientRect();
      tmp.ndc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(tmp.ndc, camera);
      // Plane normal = camera forward direction
      tmp.planeNormal.set(0, 0, -1).transformDirection(camera.matrixWorld);
      tmp.plane.setFromNormalAndCoplanarPoint(tmp.planeNormal, planeOrigin);
      const hit = raycaster.ray.intersectPlane(tmp.plane, tmp.planePt);
      return hit;
    }

    /**
     * Apply lazy mouse stabilization.  Returns the smoothed center/normal,
     * or null if the lazy radius hasn't been exceeded yet (suppress dab).
     */
    function applyLazyMouse(
      rawCenter: [number, number, number],
      rawNormal: [number, number, number],
      lazyRadius: number,
    ): {
      center: [number, number, number];
      normal: [number, number, number];
    } | null {
      if (lazyRadius <= 0) return { center: rawCenter, normal: rawNormal };

      if (!lazyPos.current) {
        // First sample: seed the lazy position
        lazyPos.current = [...rawCenter];
        lazyNormal.current = [...rawNormal];
        return { center: rawCenter, normal: rawNormal };
      }

      const dx = rawCenter[0] - lazyPos.current[0];
      const dy = rawCenter[1] - lazyPos.current[1];
      const dz = rawCenter[2] - lazyPos.current[2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < lazyRadius) return null; // Inside dead zone — suppress

      // Move the lazy point toward raw, keeping it lazyRadius behind
      const pull = 1 - lazyRadius / dist;
      lazyPos.current[0] += dx * pull;
      lazyPos.current[1] += dy * pull;
      lazyPos.current[2] += dz * pull;

      // Lerp normal toward raw and renormalize
      const t = Math.min(1, pull * 2);
      let nx =
        lazyNormal.current![0] + (rawNormal[0] - lazyNormal.current![0]) * t;
      let ny =
        lazyNormal.current![1] + (rawNormal[1] - lazyNormal.current![1]) * t;
      let nz =
        lazyNormal.current![2] + (rawNormal[2] - lazyNormal.current![2]) * t;
      const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (nLen > 1e-8) {
        nx /= nLen;
        ny /= nLen;
        nz /= nLen;
      }
      lazyNormal.current = [nx, ny, nz];

      return {
        center: [lazyPos.current[0], lazyPos.current[1], lazyPos.current[2]],
        normal: [nx, ny, nz],
      };
    }

    /**
     * Apply evenly-spaced dabs between the last dab and the current position.
     * Returns true if at least one dab was applied.
     */
    function applySpacedDabs(
      center: [number, number, number],
      normal: [number, number, number],
      pressure: number,
      objectLocalRadius: number,
      viewDirectionLocal: [number, number, number],
      meshMatrixWorld: THREE.Matrix4,
    ): boolean {
      const sm = useStore.getState().sculptMode;
      if (!sm) return false;

      // Apply lazy mouse stabilization (converts raw → smoothed position).
      // lazyRadius is in screen pixels; convert to object-local using the
      // same depth/scale logic as the brush radius, scaled down so it
      // feels proportional to the brush.
      const lazyObjRadius =
        sm.lazyRadius > 0
          ? screenToObjectLocalRadius(
              sm.lazyRadius,
              cursorRef.current.position,
              meshMatrixWorld,
              camera,
              viewportSize.height,
            ) * 0.5
          : 0;
      const stabilized = applyLazyMouse(center, normal, lazyObjRadius);
      if (!stabilized) return false;
      const stabCenter = stabilized.center;
      const stabNormal = stabilized.normal;

      const spacingDist = sm.spacing * objectLocalRadius;
      const prev = lastDab.current;

      if (!prev) {
        // First dab of the stroke — apply immediately
        useStore.getState().applySculptStroke({
          center: stabCenter,
          pressure,
          normal: stabNormal,
          radius: objectLocalRadius,
          viewDirectionLocal,
          strengthMultiplier: overlapFactor.current,
        });
        lastDab.current = { center: stabCenter, normal: stabNormal, pressure };
        return true;
      }

      const dx = stabCenter[0] - prev.center[0];
      const dy = stabCenter[1] - prev.center[1];
      const dz = stabCenter[2] - prev.center[2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < spacingDist) return false; // too close — skip

      const numDabs = Math.floor(dist / spacingDist);
      for (let i = 1; i <= numDabs; i++) {
        const t = i / numDabs;
        // Clamp t so the last interpolated dab doesn't overshoot
        const tClamped = Math.min(t, (i * spacingDist) / dist);
        const dabCenter: [number, number, number] = [
          prev.center[0] + dx * tClamped,
          prev.center[1] + dy * tClamped,
          prev.center[2] + dz * tClamped,
        ];
        // Lerp normal and renormalize
        let nx = prev.normal[0] + (stabNormal[0] - prev.normal[0]) * tClamped;
        let ny = prev.normal[1] + (stabNormal[1] - prev.normal[1]) * tClamped;
        let nz = prev.normal[2] + (stabNormal[2] - prev.normal[2]) * tClamped;
        const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (nLen > 1e-8) {
          nx /= nLen;
          ny /= nLen;
          nz /= nLen;
        }
        const dabNormal: [number, number, number] = [nx, ny, nz];
        const dabPressure =
          prev.pressure + (pressure - prev.pressure) * tClamped;

        useStore.getState().applySculptStroke({
          center: dabCenter,
          pressure: dabPressure,
          normal: dabNormal,
          radius: objectLocalRadius,
          viewDirectionLocal,
          strengthMultiplier: overlapFactor.current,
        });
      }

      // Advance the last-dab position to the final interpolated point
      const tFinal = (numDabs * spacingDist) / dist;
      lastDab.current = {
        center: [
          prev.center[0] + dx * tFinal,
          prev.center[1] + dy * tFinal,
          prev.center[2] + dz * tFinal,
        ],
        normal: stabNormal,
        pressure,
      };
      return true;
    }

    function isSculptPointer(e: PointerEvent): boolean {
      const sm = useStore.getState().sculptMode;
      if (!sm) return false;
      if (e.pointerType === "pen") return true;
      return (
        useStore.getState().allowNonPenDrawingInput &&
        (e.pointerType === "mouse" || e.pointerType === "touch")
      );
    }

    // ── Pointer handlers ─────────────────────────────────────

    function onPointerDown(e: PointerEvent) {
      if (!isSculptPointer(e)) return;
      if (e.button !== 0) return;

      const mesh = getTargetMesh();
      if (!mesh) return;

      const hit = raycastToMesh(e.clientX, e.clientY, mesh);
      if (!hit) return;

      // Delta-based undo is pushed at stroke-end inside sculptOps.ts
      // (only the affected vertices, not the entire mesh).
      strokeActive.current = true;

      // Reset lazy mouse state for new stroke
      lazyPos.current = null;
      lazyNormal.current = null;

      const localCenter = worldToLocal(hit.point, mesh);
      const localNormal = getLocalNormal(hit);
      const pressure = resolveSculptPointerPressure(e.pointerType, e.pressure);
      const sm = useStore.getState().sculptMode;
      const isGrab = sm?.brush === "grab";
      const objectLocalRadius = sm
        ? screenToObjectLocalRadius(
            sm.radius,
            hit.point,
            mesh.matrixWorld,
            camera,
            viewportSize.height,
          )
        : 0.1;

      if (isGrab) {
        overlapFactor.current = 1.0; // grab doesn't use dab spacing
        // Record anchor for grab — lock affected vertices & weights now
        grabAnchorLocal.current = localCenter;
        grabAnchorNormal.current = localNormal;
        grabWorldOrigin.current = hit.point.clone();
        grabMesh.current = mesh;
        const viewDirLocal = getViewDirectionLocal(mesh);
        useStore
          .getState()
          .initGrabSculptStroke(localCenter, objectLocalRadius, viewDirLocal);
      } else {
        // Compute per-stroke overlap factor once — constant for the stroke duration.
        overlapFactor.current = sm
          ? computeOverlapFactor(sm.spacing, sm.falloffCurve)
          : 1.0;
        // Reset spacing state and apply the first dab
        lastDab.current = null;
        const viewDirLocal = getViewDirectionLocal(mesh);
        applySpacedDabs(
          localCenter,
          localNormal,
          pressure,
          objectLocalRadius,
          viewDirLocal,
          mesh.matrixWorld,
        );
      }

      // Update cursor
      cursorRef.current.position.copy(hit.point);
      cursorRef.current.normal
        .copy(hit.face?.normal ?? new THREE.Vector3(0, 0, 1))
        .transformDirection(mesh.matrixWorld);
      cursorRef.current.visible = true;
      cursorRef.current.stroking = true;
      cursorRef.current.pressure = pressure;

      canvas.setPointerCapture(e.pointerId);
      e.preventDefault();
    }

    function onPointerMove(e: PointerEvent) {
      if (!isSculptPointer(e)) return;

      const mesh = getTargetMesh();
      if (!mesh) return;

      const sm = useStore.getState().sculptMode;
      const isGrab = sm?.brush === "grab";

      // ── Active grab stroke: project screen movement, no raycast needed ──
      if (
        strokeActive.current &&
        isGrab &&
        grabWorldOrigin.current &&
        grabAnchorLocal.current &&
        grabAnchorNormal.current &&
        grabMesh.current
      ) {
        const pressure = resolveSculptPointerPressure(e.pointerType, e.pressure);
        const worldPt = screenToPlane(
          e.clientX,
          e.clientY,
          grabWorldOrigin.current,
        );
        if (!worldPt) return;

        // Total world-space delta from the grab anchor
        tmp.worldDelta.subVectors(worldPt, grabWorldOrigin.current);
        const worldDelta = tmp.worldDelta;

        // Convert world delta direction to local space (use the mesh captured at stroke start)
        const localDelta = worldVecToLocal(worldDelta, grabMesh.current);

        // Radius for grab is computed at grab origin (stable for duration of stroke)
        const grabRadius = sm
          ? screenToObjectLocalRadius(
              sm.radius,
              grabWorldOrigin.current,
              grabMesh.current.matrixWorld,
              camera,
              viewportSize.height,
            )
          : 0.1;

        useStore.getState().applySculptStroke({
          center: grabAnchorLocal.current,
          pressure,
          normal: grabAnchorNormal.current,
          grabTotalDelta: localDelta,
          radius: grabRadius,
        });

        // Update cursor to follow pointer on the plane
        cursorRef.current.position.copy(worldPt);
        cursorRef.current.pressure = pressure;
        return;
      }

      // ── Active non-grab stroke: raycast each sample ──
      const hit = raycastToMesh(e.clientX, e.clientY, mesh);

      if (strokeActive.current && hit) {
        const localCenter = worldToLocal(hit.point, mesh);
        const localNormal = getLocalNormal(hit);
        const pressure = resolveSculptPointerPressure(e.pointerType, e.pressure);
        const objectLocalRadius = sm
          ? screenToObjectLocalRadius(
              sm.radius,
              hit.point,
              mesh.matrixWorld,
              camera,
              viewportSize.height,
            )
          : 0.1;

        const viewDirLocal = getViewDirectionLocal(mesh);
        applySpacedDabs(
          localCenter,
          localNormal,
          pressure,
          objectLocalRadius,
          viewDirLocal,
          mesh.matrixWorld,
        );

        cursorRef.current.position.copy(hit.point);
        cursorRef.current.normal
          .copy(hit.face?.normal ?? new THREE.Vector3(0, 0, 1))
          .transformDirection(mesh.matrixWorld);
        cursorRef.current.pressure = pressure;
      } else if (!strokeActive.current && hit) {
        // Hover preview
        cursorRef.current.position.copy(hit.point);
        cursorRef.current.normal
          .copy(hit.face?.normal ?? new THREE.Vector3(0, 0, 1))
          .transformDirection(mesh.matrixWorld);
        cursorRef.current.visible = true;
        cursorRef.current.stroking = false;
        cursorRef.current.pressure = 0;
      } else if (!strokeActive.current) {
        cursorRef.current.visible = false;
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (!isSculptPointer(e)) return;
      if (!strokeActive.current) return;

      strokeActive.current = false;
      lastDab.current = null;
      lazyPos.current = null;
      lazyNormal.current = null;
      grabAnchorLocal.current = null;
      grabAnchorNormal.current = null;
      grabWorldOrigin.current = null;
      grabMesh.current = null;
      cursorRef.current.stroking = false;

      // Finalise stroke — snapshots live buffers to store, bumps sculptStrokeSeq
      useStore.getState().endSculptStroke();

      canvas.releasePointerCapture(e.pointerId);
    }

    function onPointerLeave(e: PointerEvent) {
      if (!isSculptPointer(e)) return;
      if (!strokeActive.current) {
        cursorRef.current.visible = false;
      }
    }

    // Coalesce pointermove: only process the latest event per frame
    function onPointerMoveCoalesced(e: PointerEvent) {
      pendingMove = e;
      if (rafId == null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (pendingMove) {
            onPointerMove(pendingMove);
            pendingMove = null;
          }
        });
      }
    }

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMoveCoalesced);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onPointerLeave);

    return () => {
      canvas.style.touchAction = prevTouchAction;
      if (rafId != null) cancelAnimationFrame(rafId);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMoveCoalesced);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [gl, camera, scene, viewportSize, raycaster, cursorRef]);

  return null;
}
